<template>
  <v-card>
    <slot name="header" />
    <info-box
      v-if="!analyticsImpressionLoading && !isLoading && analyticsImpression === 0"
      :info-message="t('dashboard.details.no-data')"
    />
    <div v-else>
      <Loader v-if="analyticsImpressionLoading" />
      <div v-else>
        <v-card-text class="mt-1 mb-0 pb-0">
          <div class="d-md-flex justify-space-between card-text-wrapper">
            <div style="display: grid">
              <Info
                :number="analyticsImpression?.toLocaleString() || 0"
                :description="t('dashboard.details.impressions.description-overall')"
              />
              <Info
                v-if="!dashboardContext.hasFiltersApplied.value"
                :number="lastThirtyDaysAnalyticsImpressionAmount?.toLocaleString() || 0"
                :description="t('dashboard.details.impressions.description')"
              />
            </div>

            <BarChart
              :config="chartConfig"
              :series="chartSeries"
            />
          </div>
        </v-card-text>
        <Loader
          class="pt-5"
          style="height: 190px"
          size="100"
          v-if="isLoading"
        />
        <div v-else>
          <v-card-text :class="{ 'pb-0': $vuetify.display.mdAndDown }">
            <h4 class="mb-2">
              <span v-if="variables.input.specificDate">
                {{ t('dashboard.details.impressions.sub-title') }}:
                {{ formatDate(variables.input.specificDate, 'namedWeekdayWithYear', $i18n.locale) }}
              </span>
              <span v-else>{{ t('dashboard.details.impressions.main-title') }}</span>
            </h4>
            <div
              class="d-md-flex justify-flex-start flex-wrap"
              style="gap: 12px"
            >
              <Box
                v-for="trafficByUtmSource in totalTrafficByUtmSourceForCompany"
                :key="trafficByUtmSource.title"
                :title="trafficByUtmSource.title"
                :items="trafficByUtmSource.values"
                :width="$vuetify.display.mdAndDown ? '100%' : '19%'"
              />
              <Box
                v-if="!totalTrafficByUtmSourceForCompany?.length"
                :title="t('dashboard.details.impressions.utm-params.title')"
                :items="[]"
                :width="$vuetify.display.mdAndDown ? '100%' : '19%'"
              />
              <br v-if="$vuetify.display.mdAndUp" />
            </div>
          </v-card-text>
          <v-card-text class="pt-0">
            <div class="d-md-flex justify-start">
              <Box
                class="mr-md-4"
                :title="t('dashboard.details.impressions.traffic-source.title')"
                :items="totalTrafficBySourceForCompany"
                :width="$vuetify.display.mdAndDown ? '100%' : '39%'"
              />
            </div>
          </v-card-text>
        </div>
      </div>
    </div>
    <slot name="footer" />
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import InfoBox from '@/components/InfoBox.vue'
import { capitalizeFirstLetter } from '@/lib/capitalize'
import { createBarChartConfig } from '@/modules/dashboard/dashboardDetails/lib/barChartConfig'
import DAILY_IMPRESSIONS from '@/modules/dashboard/dashboardDetails/queries/dailyImpressions.gql'
import BarChart from '@/modules/dashboard/dashboardDetails/shared/barChart.vue'
import Box from '@/modules/dashboard/dashboardDetails/shared/box.vue'
import Info from '@/modules/dashboard/dashboardDetails/shared/info.vue'
import Loader from '@/modules/dashboard/dashboardDetails/shared/loader.vue'
import GET_ANALYTICS_IMPRESSIONS from '@/modules/dashboard/GetDailyAnalyticsImpressionForCompany.gql'
import TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY from '@/modules/leads/queries/TotalTrafficBySourceForCompany.gql'
import TOTAL_TRAFFIC_BY_UTM_SOURCE_FOR_COMPANY from '@/modules/leads/queries/TotalTrafficByUtmSourceForCompany.gql'

import { formatDate } from '../../../lib/dateTime'
import {
  DashboardContext,
  dashboardContextDefault,
  DashboardContextSymbol,
} from '../dashboardContext'

const { t } = useI18n()

const dashboardContext = inject<DashboardContext>(DashboardContextSymbol, dashboardContextDefault)

const lastSelectedDate = ref(null)

const route = useRoute()
const routeParamId = computed(() => route?.params?.id || null)
const isMasterView = computed(() => route.path.startsWith('/master'))
const analyticsImpressionVariables = computed(() => ({
  input: {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value ?? null,
    ...dashboardContext.formattedDateFilters.value,
  },
}))

const { result: analytics, loading: analyticsImpressionLoading } = useQuery(
  GET_ANALYTICS_IMPRESSIONS,
  analyticsImpressionVariables
)

const thirtyDaysAnalyticsImpressionVariables = computed(() => {
  const hasDateFilters = dashboardContext.dateFilters.value?.length === 2

  if (hasDateFilters) {
    return {
      input: {
        isMasterView: isMasterView.value,
        childCompanyId: routeParamId.value ?? null,
        ...dashboardContext.formattedDateFilters.value,
      },
    }
  }

  const today = new Date()
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(today.getDate() - 30)
  thirtyDaysAgo.setHours(0, 0, 0, 0)

  const endOfToday = new Date(today)
  endOfToday.setHours(23, 59, 59, 999)

  return {
    input: {
      isMasterView: isMasterView.value,
      childCompanyId: routeParamId.value ?? null,
      filters: {
        startDate: thirtyDaysAgo.toISOString(),
        endDate: endOfToday.toISOString(),
      },
    },
  }
})

const { result: lastThirtyDaysAnalyticsImpression } = useQuery(
  DAILY_IMPRESSIONS,
  thirtyDaysAnalyticsImpressionVariables
)

const lastThirtyDaysAnalyticsImpressionAmount = computed(() => {
  if (!lastThirtyDaysAnalyticsImpression.value?.dailyAnalyticsImpressions) return 0
  return lastThirtyDaysAnalyticsImpression.value.dailyAnalyticsImpressions.reduce(
    (acc, curr) => acc + curr.impressions,
    0
  )
})

const analyticsImpression = computed(
  () => analytics.value?.dailyAnalyticsImpressionForCompany?.impressions
)

const variables = reactive({
  input: {
    specificDate: null,
    last30Days: false,
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
    ...dashboardContext.formattedDateFilters.value,
  },
})

const { result: totalTrafficBySource, loading: trafficSourceLoading } = useQuery(
  TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY,
  variables
)

const directSources = computed(() => {
  const total = totalTrafficBySource.value?.totalTrafficBySourceForCompany?.find(
    (item) => !item.source
  )

  return total ? { label: t('dashboard.details.impressions.utm-direct'), value: total.count } : null
})

const totalTrafficBySourceForCompany = computed(() => {
  const readableItems =
    totalTrafficBySource.value?.totalTrafficBySourceForCompany
      ?.filter((item) => item.source)
      .map((item) => ({
        label: capitalizeFirstLetter(item.source),
        value: item.count,
      })) || []

  const result = [...readableItems]
  if (directSources.value) {
    result.push(directSources.value)
  }

  return result.sort((a, b) => b.value - a.value)
})
const { result: totalTrafficByUtmSource, loading: utmSourceLoading } = useQuery(
  TOTAL_TRAFFIC_BY_UTM_SOURCE_FOR_COMPANY,
  variables
)

// Combine views (analyticsImpression.utmTrafficCounts) and conversions (totalTrafficByUtmSourceForCompany) by utmKey and label
const totalTrafficByUtmSourceForCompany = computed(() => {
  const viewsData = analytics.value?.dailyAnalyticsImpressionForCompany?.utmTrafficCounts || []
  const conversionsData = totalTrafficByUtmSource.value?.totalTrafficByUtmSourceForCompany || []
  return viewsData
    .map((viewsItem: any) => {
      const utmKey = viewsItem.utmKey
      const conversionsItem = conversionsData.find((c: any) => c.utmKey === utmKey)
      const values = viewsItem.values.map((view: any) => {
        const label = view.label
        const views = view.count
        const conversionObj = conversionsItem?.values.find((c: any) => c.label === label)
        const conversions = conversionObj ? conversionObj.count : 0
        return { label, conversions, views, value: `${conversions} / ${views}` }
      })
      return {
        title: 'utm-' + utmKey,
        values,
      }
    })
    .sort((a: any, b: any) => {
      const sum = (arr: any[]) => arr.reduce((acc: number, v: any) => acc + v.views, 0)
      return sum(b.values) - sum(a.values)
    })
})

const isLoading = computed(() => trafficSourceLoading.value || utmSourceLoading.value)

const handleSelect = (date) => {
  if (date === lastSelectedDate.value) {
    variables.input.specificDate = null
    lastSelectedDate.value = null
    return
  }

  variables.input.specificDate = date
  lastSelectedDate.value = date
}

const chartConfig = computed(() => {
  return createBarChartConfig({
    //@ts-expect-error:
    chartSeries: chartSeries,
    tooltipText: 'dashboard.details.impressions.chart.tooltip',
    onSelect: handleSelect,
  })
})

const chartSeries = computed(() => {
  const dateFilters = dashboardContext.dateFilters.value
  const dailyImpressions = lastThirtyDaysAnalyticsImpression.value?.dailyAnalyticsImpressions || []

  let startDate, endDate
  if (dateFilters?.length === 2) {
    startDate = new Date(dateFilters[0])
    endDate = new Date(dateFilters[1])
  } else {
    endDate = new Date()
    startDate = new Date()
    startDate.setDate(endDate.getDate() - 29)
  }

  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

  const impressionsByDay: Record<string, number> = {}
  dailyImpressions.forEach((item) => {
    const dateKey = new Date(item.createdAt).toISOString().split('T')[0]
    impressionsByDay[dateKey] = item.impressions
  })

  const data = Array.from({ length: daysDiff }, (_, i) => {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    const key = date.toISOString().split('T')[0]
    const y = impressionsByDay[key] ?? 0
    return { x: key, y }
  })

  return [
    {
      name: 'Chart Series',
      data,
    },
  ]
})
</script>
<style scoped>
.card-text-wrapper {
  border-radius: 10px;
  box-shadow: 0 11px 15px 0 rgba(0, 0, 0, 0.04);
}
</style>
